/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { Button } from "@nordcloud/gnui";
import { ContactRolesSelect } from "~/components/Contacts/ContactRolesSelect";
import { TextAreaWrapper } from "~/components/Contacts/wrappers";
import { useShowError } from "~/components/Error/useShowError";
import { SidebarTextField } from "~/components/SidebarForm/inputs/SidebarTextField";
import { SidebarForm } from "~/components/SidebarForm/SidebarForm";
import { SidebarFormButtons } from "~/components/SidebarForm/SidebarFormButtons";
import { showSuccess } from "~/components/Toast";
import { Contact, RouteParams } from "~/models";
import { generateActionSuccessText, isValidEmail, useAxios } from "~/tools";
import { isValidPhone } from "~/tools/validation/checkPhone";
import { maxLength } from "~/tools/validation/validators";

type ContactEditProps = {
  refetch: () => void;
  contactId: string;
};

const contactInitialState: Contact = {
  comment: null,
  email: "",
  externalRoles: [],
  id: "",
  name: "",
  phoneNumber: null,
  position: "",
  rulesOfEngagement: null,
};

function ContactEdit({ refetch, contactId }: ContactEditProps) {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);

  const methods = useForm();
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const watcher = methods.watch();

  const { customerId } = useParams<RouteParams>();

  const [contact, setContact] = React.useState<Contact>(contactInitialState);

  const fetchContact = React.useCallback(async () => {
    try {
      const { data } = await Axios().get(
        `/customers/${customerId}/contacts/${contactId}`
      );
      setContact(data);
    } catch (e) {
      showError(e.response);
    }
  }, [Axios, customerId, contactId, showError]);

  React.useEffect(() => {
    isSidebarOpen && fetchContact();
  }, [fetchContact, isSidebarOpen]);

  const onSubmit = async (values: Contact) => {
    try {
      await Axios().put(
        `/customers/${customerId}/contacts/${contactId}`,
        values
      );
      showSuccess(generateActionSuccessText(`Contact`)()("updated")());
    } catch (e) {
      showError(e.response);
    } finally {
      clean();
      setIsSidebarOpen(false);
    }
  };

  const clean = () => {
    refetch && refetch();
  };

  return (
    <>
      <Button
        icon="edit"
        severity="low"
        size="sm"
        css={{ margin: "0.5rem" }}
        onClick={() => setIsSidebarOpen(true)}
      >
        Edit
      </Button>

      <SidebarForm
        title="Edit Contact"
        width="36rem"
        open={isSidebarOpen}
        setOpen={setIsSidebarOpen}
        formMethods={methods}
        submit={onSubmit}
      >
        <SidebarTextField
          name="name"
          label="Name"
          defaultValue={contact.name}
          required
        />
        <ContactRolesSelect currentRoles={contact.externalRoles} />
        <SidebarTextField
          name="position"
          label="Position"
          defaultValue={contact.position}
          required
        />
        <SidebarTextField
          name="email"
          label="Email"
          defaultValue={contact.email}
          required
          registerOptions={{ validate: isValidEmail }}
        />
        <SidebarTextField
          name="phoneNumber"
          label="Phone number"
          defaultValue={contact.phoneNumber || ""}
          registerOptions={{ validate: isValidPhone }}
        />
        <TextAreaWrapper>
          <SidebarTextField
            textArea
            name="comment"
            label="Comment"
            defaultValue={contact.comment || ""}
            registerOptions={maxLength(256)}
          />
        </TextAreaWrapper>
        <TextAreaWrapper>
          <SidebarTextField
            textArea
            name="rulesOfEngagement"
            label="Rules of engagement"
            defaultValue={contact.rulesOfEngagement || ""}
            registerOptions={maxLength(600)}
          />
        </TextAreaWrapper>

        <SidebarFormButtons
          submitEnabled={!!(watcher.name && watcher.position && watcher.email)}
          onCancel={() => setIsSidebarOpen(false)}
        />
      </SidebarForm>
    </>
  );
}

export { ContactEdit };
